import {
  combineReducers,
  configureStore,
  createAction,
  UnknownAction,
} from "@reduxjs/toolkit";
import { configReducer } from "./slices/configSlice";
import { userReducer } from "./slices/userSlice";
import { frameReducer } from "./slices/frameSlice";
import { launcherReducer } from "./slices/launcherSlice";
import { challengeReducer } from "./slices/challengeSlice";
import { opengraphReducer } from "./slices/opengraphSlice";
import { chatReducer } from "./slices/chatSlice";
import { conversationsReducer } from "./slices/conversationSlice";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { promptReducer } from "./slices/promptSlice";

import { Middleware } from "@reduxjs/toolkit";
import {
  createLocaleMiddleware,
  createSdkEventMiddleware,
  createSyncUserDataMiddleware,
  createThemeRefreshMiddleware,
} from "./actions/middlewares";
import { signalReducer } from "./slices/signalSlice";
import { handoffReducer } from "./slices/handoffSlice";

const errorLoggerMiddleware: Middleware = (store) => (next) => (action) => {
  try {
    return next(action);
  } catch (error) {
    // Log the error with action details for debugging
    console.error("Redux Error:", {
      action: action.type,
      payload: (action as any).payload,
      error: {
        message: (error as Error).message,
        stack: (error as Error).stack,
      },
    });
    // Re-throw the error so it doesn't get swallowed
    throw error;
  }
};

export const resetEntireStore = createAction("root/resetStore");
export const hydrateEntireStore =
  createAction<Partial<RootState>>("root/hydrateStore");

const rootReducers = combineReducers({
  config: configReducer,
  user: userReducer,
  frame: frameReducer,
  launcher: launcherReducer,
  challenge: challengeReducer,
  opengraph: opengraphReducer,
  prompt: promptReducer,
  signal: signalReducer,
  chat: chatReducer,
  conversations: conversationsReducer,
  handoff: handoffReducer,
});

const rootReducer = (
  state: ReturnType<typeof rootReducers> | undefined,
  action: UnknownAction
): ReturnType<typeof rootReducers> => {
  if (action.type === resetEntireStore.type) {
    state = undefined;
    return rootReducers(state, action);
  } else if (action.type === hydrateEntireStore.type) {
    state = action.payload as ReturnType<typeof rootReducers>;
    return rootReducers(state, action);
  }
  return rootReducers(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(errorLoggerMiddleware)
      .concat(createThemeRefreshMiddleware())
      .concat(createLocaleMiddleware())
      .concat(createSyncUserDataMiddleware())
      .concat(createSdkEventMiddleware()),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

if (process.env.NODE_ENV === "development") {
  // @ts-ignore
  window.store = store;
}
